import React, { useState } from 'react';
import axios from 'axios';
import PublicLayout from '../components/public/PublicLayout';
import { PageHero } from './UniversitiesListPage';
import { Mail, Phone, MapPin, Send, CheckCircle2, Loader2 } from 'lucide-react';
import { useSiteSettings } from '../hooks/useSiteSettings';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactPage = () => {
  const site = useSiteSettings();
  const [form, setForm] = useState({ name: '', email: '', phone: '', country: '', interest: '', message: '' });
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error
  const [error, setError] = useState('');

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    setError('');
    try {
      await axios.post(`${API}/leads/contact`, {
        name: form.name,
        email: form.email,
        phone: form.phone || undefined,
        country: form.country || undefined,
        interest: form.interest || undefined,
        message: form.message,
        subject: 'Website Contact Form',
      });
      setStatus('sent');
      setForm({ name: '', email: '', phone: '', country: '', interest: '', message: '' });
    } catch (err) {
      setStatus('error');
      setError(err?.response?.data?.detail || 'Could not submit. Try again.');
    }
  };

  return (
    <PublicLayout>
      <PageHero title="Contact Us" subtitle="Tell us about your study-abroad goals — a counsellor will get back within 24 hours." />

      <section className="max-w-7xl mx-auto px-4 py-12 grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Contact info cards */}
        <div className="lg:col-span-1 space-y-4">
          {site.secondary_address && (
            <InfoCard icon={<MapPin className="w-5 h-5" />} title="Visit us" lines={site.secondary_address.split('\n').filter(Boolean)} />
          )}
          {site.primary_address && (
            <InfoCard icon={<MapPin className="w-5 h-5" />} title="Head office" lines={site.primary_address.split('\n').filter(Boolean)} />
          )}
          {(site.phones || []).length > 0 && (
            <InfoCard icon={<Phone className="w-5 h-5" />} title="Call us" lines={site.phones} />
          )}
          {(site.emails || []).length > 0 && (
            <InfoCard icon={<Mail className="w-5 h-5" />} title="Email" lines={site.emails} />
          )}
        </div>

        {/* Form */}
        <div className="lg:col-span-2">
          <div className="bg-white border border-gray-100 rounded-2xl p-6 sm:p-8 shadow-sm">
            {status === 'sent' ? (
              <div className="text-center py-10" data-testid="contact-success">
                <CheckCircle2 className="w-14 h-14 text-[#F5A623] mx-auto" />
                <h3 className="mt-4 text-2xl font-bold text-[#1A2A4F]">Thanks — we've got your message!</h3>
                <p className="mt-2 text-gray-600">A counsellor will be in touch within 24 hours.</p>
                <button onClick={() => setStatus('idle')} className="mt-5 text-sm font-semibold text-[#F5A623] hover:underline" data-testid="contact-send-another">
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="contact-form">
                <Field label="Your name *" testid="contact-name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
                <Field label="Email *" testid="contact-email" required type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
                <Field label="Phone" testid="contact-phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
                <Field label="Preferred country" testid="contact-country" value={form.country} onChange={(v) => setForm({ ...form, country: v })} />
                <div className="sm:col-span-2">
                  <Field label="Subject / interest" testid="contact-interest" value={form.interest} onChange={(v) => setForm({ ...form, interest: v })} />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-[#1A2A4F] mb-1.5">Message *</label>
                  <textarea
                    required
                    rows={5}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="Tell us what you're looking for..."
                    className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#F5A623]/20 focus:border-[#F5A623] outline-none"
                    data-testid="contact-message"
                  />
                </div>
                {status === 'error' && <p className="sm:col-span-2 text-sm text-red-600" data-testid="contact-error">{error}</p>}
                <div className="sm:col-span-2 flex">
                  <button
                    type="submit"
                    disabled={status === 'sending'}
                    className="bg-[#F5A623] text-white font-semibold px-7 py-3 rounded-md hover:brightness-95 disabled:opacity-60 inline-flex items-center gap-2 transition"
                    data-testid="contact-submit-btn"
                  >
                    {status === 'sending' ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <><Send className="w-4 h-4" /> Send message</>}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </PublicLayout>
  );
};

const Field = ({ label, value, onChange, required, type = 'text', testid }) => (
  <div>
    <label className="block text-sm font-medium text-[#1A2A4F] mb-1.5">{label}</label>
    <input
      type={type}
      required={required}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#F5A623]/20 focus:border-[#F5A623] outline-none"
      data-testid={testid}
    />
  </div>
);

const InfoCard = ({ icon, title, lines }) => (
  <div className="bg-white border border-gray-100 rounded-xl p-5">
    <div className="flex items-start gap-3">
      <div className="w-10 h-10 rounded-full bg-[#F5A623]/10 text-[#F5A623] flex items-center justify-center shrink-0">{icon}</div>
      <div>
        <h3 className="font-bold text-[#1A2A4F]">{title}</h3>
        {lines.map((l, i) => (<p key={i} className="text-sm text-gray-600 mt-1">{l}</p>))}
      </div>
    </div>
  </div>
);

export default ContactPage;
